import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/shared/ui/Accordion";
import {
  Table, TableBody, TableRow, TableCell,
} from "@/shared/ui/Table";
import { formatCommentsToJobResults } from "@/shared/utils/formatter.utils";
import type { Job, JobResult } from "../../types/jobs.types";

type JobResultsProps = {
  result: Job["result"]
  fallbackComments: string
};

const JobResults = ({
  result,
  fallbackComments,
}: JobResultsProps) => {
  const hasErrorList = !!result.errors?.length;
  const hasSuccessList = !!result.success?.length;
  const hasToVerifyList = !!result.to_verify?.length;
  const hasAtLeastTwoLists = (hasErrorList && hasSuccessList) || (hasErrorList && hasToVerifyList) || (hasSuccessList && hasToVerifyList);

  const defineDefaultAccordionValue = (): string => {
    if (isFallBackCommentsVisible) return "comments";
    if (hasAtLeastTwoLists) return "";
    if (hasErrorList) return "errors";
    if (hasSuccessList) return "success";
    if (hasToVerifyList) return "verify";
    return "";
  };

  const fallbackCommentsTitle = "Résultats :";
  const isFallBackCommentsVisible = !hasSuccessList && !hasErrorList && !hasToVerifyList && !!fallbackComments;

  if (isFallBackCommentsVisible) {
    const fallbackCommentsList: JobResult[] = formatCommentsToJobResults(fallbackComments);

    return !!fallbackCommentsList?.length && (
      <Accordion type="single" collapsible defaultValue={defineDefaultAccordionValue()}>
        <AccordionItem value="comments" key="comments">
          <AccordionTrigger className="w-full bg-gray-100 px-6 rounded-none">{fallbackCommentsTitle}</AccordionTrigger>
          <AccordionContent className="max-h-96 relative overflow-x-auto">
            <Table>
              <TableBody>
                {fallbackCommentsList.map(({ id, subject, message }) => (
                  <TableRow key={id}>
                    <TableCell data-item-id={id} className="whitespace-pre-wrap flex flex-col text-xs px-12">
                      <span>{subject}</span>
                      <span>{message}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }

  const resultLists = [
    {
      title: `${result.to_verify_count} lignes à vérifier`,
      key: "verify",
      list: result.to_verify,
    },
    {
      title: `${result.error_count} erreurs rencontrées`,
      key: "errors",
      list: result.errors,
    },
    {
      title: result.created_count !== undefined && result.updated_count !== undefined
        ? `${result.success_count} lignes traitées avec succès (${result.created_count} créations, ${result.updated_count} mises à jour)`
        : `${result.success_count} lignes traitées avec succès`,
      key: "success",
      list: result.success,
    },
  ] as const;

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defineDefaultAccordionValue()}
    >
      {resultLists.map(({ title, key, list }) => !!list?.length && (
        <AccordionItem value={key} key={key}>
          <AccordionTrigger className="w-full bg-gray-100 px-6 rounded-none">{title}</AccordionTrigger>
          <AccordionContent className="max-h-96 relative overflow-x-auto">
            <Table className="w-full table-auto">
              <TableBody>
                {list.map(({ id, subject, message }) => (
                  <TableRow key={id}>
                    <TableCell data-item-id={id} className="whitespace-pre-wrap flex flex-col text-xs px-12">
                      <span>{subject}</span>
                      <span>{message}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default JobResults;
